import { motion } from 'framer-motion';
import Popover from '@atoms/Popover/Popover';
import usePopover from '@hook/usePopover';
import { Box as MuiBox, Stack as MuiStack, SvgIcon } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuItem from '@atoms/MenuItem/MenuItem';
import { TopNavAvatarButtonProps } from './TopNavAvatarButton.types';
import { useTranslation } from 'react-i18next';
import Typography from '@atoms/Typography/Typography';
import Avatar from '@atoms/Avatar/Avatar';
import { IconButton } from '@atoms/IconButton';
import Divider from '@atoms/Divider/Divider';
import { useAuthContext } from '@contexts/AuthContext';
import { Switch } from '@atoms/Switch';
import {
  DEV_MODE_COOKIE_NAME,
  IS_PRODUCT,
  NAREA_PROFILE_URL,
  XYZ_MEMBER,
} from '@utils/const';
import { getCookie, setCookie } from '@utils/cookie';
import { ReactComponent as MoveIcon } from '@assets/icons/ic-move.svg';
import { ReactComponent as SettingIcon } from '@assets/icons/ic-settings.svg';

export default function TopNavAvatarButton({
  imgUrl,
  displayName,
  email,
}: TopNavAvatarButtonProps) {
  const popover = usePopover();
  const { t } = useTranslation();
  const { logout } = useAuthContext();
  const { iam } = useAuthContext();

  const isXyzMember = !IS_PRODUCT && iam && XYZ_MEMBER.includes(iam?.email);

  const isCheck = getCookie(DEV_MODE_COOKIE_NAME) === 'true';

  const handleMode = (event) => {
    const { checked } = event.target;
    setCookie(DEV_MODE_COOKIE_NAME, checked, { path: '/' });
    window.location.reload();
  };

  return (
    <>
      <IconButton
        component={motion.button}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={imgUrl}
          alt={displayName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        />
      </IconButton>
      <Popover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ width: 200, p: 0 }}
      >
        <MuiBox sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {displayName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {email}
          </Typography>
        </MuiBox>

        <Divider dashed />

        <MenuItem
          onClick={() => {
            window.open(NAREA_PROFILE_URL);
          }}
          sx={{
            m: 1,
            fontWeight: 'fontWeightBold',
            color: 'text.primary',
            '&.MuiMenuItem-root svg': { marginRight: 1 },
          }}
        >
          <SvgIcon>
            <SettingIcon />
          </SvgIcon>
          {t('프로필 수정')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            logout();
          }}
          sx={{
            m: 1,
            fontWeight: 'fontWeightBold',
            color: 'error.main',
            '&.MuiMenuItem-root svg': { marginRight: 1 },
          }}
        >
          <SvgIcon>
            <MoveIcon />
          </SvgIcon>
          {t('로그아웃')}
        </MenuItem>
        {isXyzMember && (
          <MuiBox sx={{ p: 2, pb: 1.5 }}>
            <MuiStack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2" noWrap>
                개발자 모드
              </Typography>
              <Switch onChange={handleMode} checked={isCheck} />
            </MuiStack>
          </MuiBox>
        )}
      </Popover>
    </>
  );
}
